/**
 * SpanDetailDrawer — versión side-mounted del SpanDetailPane (Fase 42).
 *
 * Reusa el contenido del SpanDetailPane existente pero lo embebe en un
 * drawer fijo a la derecha que se puede cerrar con ESC, backdrop click o el
 * botón X. Pensado para la vista Live: permite seguir scrolleando el
 * timeline mientras se inspecciona un span.
 *
 * No tocamos `SpanDetailPane` (el de Waterfall sigue usándolo como antes).
 * Si en una futura iteración decidimos consolidar, deprecamos el pane
 * inline en un PR separado.
 */
import { useEffect, type ReactElement } from 'react';
import type { TimelineSpan } from '../../api/admin.js';
import type { LiveSpanCloseEvent } from '../../lib/useLiveSession.js';
import { SpanDetailPane } from './SpanDetailPane.js';
import { LiveSpanDetail } from './LiveSpanDetail.js';

export interface SpanDetailDrawerProps {
  span: TimelineSpan | null;
  /**
   * Evento crudo del bus para este span (si existe). Cuando se pasa, el
   * drawer renderea la vista especializada por kind (`LiveSpanDetail`)
   * que usa `event.input`/`event.output` directos del bus. Cuando es
   * `undefined`, cae a la vista genérica `SpanDetailPane` (fetch DB).
   */
  liveEvent?: LiveSpanCloseEvent | undefined;
  onClose: () => void;
}

export function SpanDetailDrawer({
  span,
  liveEvent,
  onClose,
}: SpanDetailDrawerProps): ReactElement | null {
  // ESC cierra el drawer.
  useEffect(() => {
    if (!span) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [span, onClose]);

  if (!span) return null;

  return (
    <>
      {/* Backdrop semitransparente. Click cierra. No bloquea el timeline
          visualmente — es bien tenue para que el contexto siga visible. */}
      <div
        className="span-drawer-backdrop"
        onClick={onClose}
        aria-hidden="true"
        data-testid="span-drawer-backdrop"
      />
      <div
        className="span-drawer"
        role="dialog"
        aria-label={`Detalle del span ${span.name}`}
        data-testid="span-drawer"
      >
        {liveEvent ? (
          <LiveSpanDetail span={span} liveEvent={liveEvent} onClose={onClose} />
        ) : (
          <SpanDetailPane span={span} onClose={onClose} />
        )}
      </div>
    </>
  );
}
